<template>
    <div class="text-sm">
        <p class="modal-heading mb-4">Confirm patient details</p>

        <div class="row">
            <div class="col-md-3">Name</div>
            <div class="col-md-9 text-capitalize">{{ `${patient.firstName} ${patient.middleNames ? patient.middleNames : ''} ${patient.surname}` }}</div>
        </div>

        <hr>

        <div v-if="patient.dateOfBirth">
            <div class="row">
                <div class="col-md-3">DOB</div>
                <div class="col-md-9">{{ patient.dateOfBirth | moment("DD/MM/YYYY") }}</div>
            </div>

            <hr>
        </div>

        <div class="row">
            <div class="col-md-3">{{ patientIdentifierLabel }}</div>
            <div class="col-md-9">{{ patient.identifier }}</div>
        </div>

        <hr>

        <div class="row">
            <div class="col-md-3">Phone number</div>
            <div class="col-md-9">{{ patient.phone }}</div>
        </div>

        <hr>

        <div class="row">
            <div class="col-md-3">Email</div>
            <div class="col-md-9">{{ patient.email }}</div>
        </div>

        <hr>

        <div class="row">
            <div class="col-md-3">Address</div>
            <div class="col-md-9">
                {{ formatAddress(address) }}
            </div>
        </div>

        <hr>

        <div v-if="patient.height">
            <div class="row">
                <div class="col-md-3">Height</div>
                <div v-if="this.feet || patient.height" class="col-md-9">{{ `${this.feet ? this.feet : 0}ft ${this.inches ? this.inches : 0}in` }} | {{ patient.height }}cm</div>
            </div>

            <hr>
        </div>

        <div v-if="patient.weight">
            <div class="row">
                <div class="col-md-3">Weight</div>
                <div v-if="this.stones || patient.weight" class="col-md-9">{{ `${this.stones ? this.stones : 0}st ${this.pounds ? this.pounds : 0}lbs` }} | {{ patient.weight }}kg</div>
            </div>

            <hr>
        </div>

        <div class="row">
            <div class="col-md-3">Sex</div>
            <div v-if="patient.gender == Gender.MALE" class="col-md-9">Male</div>
            <div v-if="patient.gender == Gender.FEMALE" class="col-md-9">Female</div>
            <div v-if="patient.gender == Gender.OTHER" class="col-md-9">Other / prefer not to say</div>
        </div>

        <hr>

        <div class="row">
            <div class="col-md-3">Ethnicity group</div>
            <div class="col-md-9">
                <div v-if="patient.primaryEthnicity != null && typeof patient.primaryEthnicity === 'object'">Primary ethnicity: {{ patient.primaryEthnicity.label }}</div>
                <div v-if="patient.secondaryEthnicity != null && typeof patient.secondaryEthnicity === 'object'">Secondary ethnicity: {{ patient.secondaryEthnicity.label }}</div>
                <div v-if="patient.tertiaryEthnicity != null && typeof patient.tertiaryEthnicity === 'object'">Tertiary ethnicity: {{ patient.tertiaryEthnicity.label }}</div>
            </div>
        </div>

        <hr>

        <div class="row">
            <div class="col-md-3">Preferred Language</div>
            <div class="col-md-9">{{ formatLanguageLabel(patient.preferredLanguage) }}</div>
        </div>

        <hr>

        <div v-if="error != null" class="mt-4 alert alert-danger text-pre-formatted">{{ error }}</div>

        <div v-if="warning != null" class="mt-4 alert alert-warning text-pre-formatted">{{ warning }}</div>

        <div class="d-flex align-items-center mt-4">
            <button :disabled="loading" @click="close()" class="btn btn-cancel btn-lg lg-shadow d-flex flex-grow-1 justify-content-center align-items-center" :class="{disabled : loading}">Cancel</button>
            <button :disabled="loading || is409" @click="createPatient()" class="btn btn-success btn-lg lg-shadow d-flex flex-grow-1 ml-3 justify-content-center align-items-center" :class="{disabled : loading || is409}">Confirm <span v-if="loading" class="btn-loading"></span></button>
        </div>
    </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import axios from 'axios'
import Gender from '../../../../Common/enums/Gender'
import FormatterMixin from '../../../../Common/mixins/FormatterMixin'

export default {
    name: 'ConfirmPatientDetailsModalComponent', 

    data() {
        return {
            loading: false,
            error: null,
            is409: false,
            Gender,
            isPhoneDuplicate: false,
            isEmailDuplicate: false,
            warning: null
        }
    },

    props: {
        patient: {},
        stones: Number,
        pounds: Number,
        feet: Number,
        inches: Number,
        updateMode: Boolean
    },

    mixins: [
        FormatterMixin
    ],

    computed: {
        ...mapGetters({
            organisationId: 'UserStore/GetUserOrganisationId',
            patientIdentifierLabel: 'OrganisationStore/GetPatientIdentifierLabel',
            languages: 'LanguageStore/GetLanguages',
            address: 'AddressFormStore/GetAddress',
        })
    },

    methods: {
        ...mapActions({
            setPatientDetailsInTestStore: 'TestStore/SetPatientDetails',
            setNotification: 'NotificationStore/SetNotification'
        }),

        async createPatient() {  
            this.loading = true
            this.error = null
            this.warning = null

            var primaryEthnicity = null
            var secondaryEthnicity = null
            var tertiaryEthnicity = null

            if(this.patient.primaryEthnicity != null && typeof this.patient.primaryEthnicity === 'object') {
                primaryEthnicity = this.patient.primaryEthnicity.code
            }

            if(this.patient.secondaryEthnicity != null && typeof this.patient.secondaryEthnicity === 'object') {
                secondaryEthnicity = this.patient.secondaryEthnicity.code
            }

            if(this.patient.tertiaryEthnicity != null && typeof this.patient.tertiaryEthnicity === 'object') {
                tertiaryEthnicity = this.patient.tertiaryEthnicity.code
            }
        
            let createPatientModel = {
                title: this.patient.title,
                firstName: this.patient.firstName,
                middleNames: this.patient.middleNames,
                surname: this.patient.surname,
                dateOfBirth: this.patient.dateOfBirth,
                identifier: this.patient.identifier,
                phone: this.patient.phone,
                email: this.patient.email,
                height: this.patient.height,
                weight: this.patient.weight,
                gender: this.patient.gender,
                ethnicity: this.patient.ethnicity,
                primaryEthnicity: primaryEthnicity,
                secondaryEthnicity: secondaryEthnicity,
                tertiaryEthnicity: tertiaryEthnicity,
                preferredLanguage: this.patient.preferredLanguage,
                address: this.address
            }

            if (this.isPhoneDuplicate) {
                // We've attempted to push up a patient with a duplicate phone number once, and the user has decided after reading the message that they wish to continue
                createPatientModel.AllowDuplicatePhoneNumber = true
            }

            if (this.isEmailDuplicate) {
                // We've attempted to push up a patient with a duplicate email address once, and the user has decided after reading the message that they wish to continue
                createPatientModel.AllowDuplicateEmail = true
            }

            try {
                let response = null;
                if (this.updateMode) {
                    response = await axios.patch(`${process.env.VUE_APP_AXIOS_PATIENT_BASE_URL}/api/v1/patients/${this.organisationId}/${this.$route.params.id}`, createPatientModel)
                }
                else {
                    response = await axios.post(`${process.env.VUE_APP_AXIOS_PATIENT_BASE_URL}/api/v1/patients/${this.organisationId}`, createPatientModel)
                }

                this.setPatientDetailsInTestStore(response.data)

                this.$emit('close')
                
                if (this.updateMode) {
                    this.setNotification({
                        type: 'success',
                        title: 'Patient updated',
                        message: 'Saved successfully!'
                    })
                }
                else {
                    return this.$router.push({name: 'PatientCreated'})
                }
            }
            catch(err) {
                if(err.data.status == 409) {
                    if (err.data.detail.includes('identifier')) {
                        this.error = `A patient with the same ${this.patientIdentifierLabel} already exists`
                        this.is409 = true
                    }
                    else if (err.data.detail.includes('email') && err.data.detail.includes('phone')) {
                        this.warning = err.data.detail + '\n\nClick Confirm if you wish to use these duplicate phone number and email address values'
                        this.isPhoneDuplicate = true
                        this.isEmailDuplicate = true
                        this.is409 = false
                    }
                    else if (err.data.detail.includes('phone')) {
                        this.warning = err.data.detail + '\n\nClick Confirm if you wish to use this duplicate phone number'
                        this.isPhoneDuplicate = true
                        this.is409 = false
                    }
                    else if (err.data.detail.includes('email')) {
                        this.warning = err.data.detail + '\n\nClick Confirm if you wish to use this duplicate email address'
                        this.isEmailDuplicate = true
                        this.is409 = false
                    }
                    else {
                        this.error = err.data.detail
                        this.is409 = true
                    }
                } else {
                    this.error = 'Sorry, something went wrong. Please try again.'
                }
            }
            finally {
                this.loading = false
            }
        },

        close() {
            this.$emit('close')
        }
    }
}
</script>